import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { bankTransactionLabel, bankTransactionDate } from '../utils/bankTransactionText';
import { proposalFromTemplateResponse, proposalsFromResponse } from '../services/BookingProposalService';
import {
    getBookkeepingTemplate,
    listBookkeepingTemplates,
    recordTemplateUsage,
    suggestVerification as suggestFromTemplate,
} from '../services/BookkeepingTemplateService';
import { resolveRoleAccounts } from '../services/api/chartRolesApi';
import useBookingProposalReview from './useBookingProposalReview';

const round2 = (n) => Math.round(n * 100) / 100;
export const emptyRow = () => ({ account_number: '', account_name: '', debit: '', credit: '' });

// Series is V for all verifikat created here (owner decision 2026-07-26:
// the company books everything in one V series — not a user choice for now).
export const VERIFICATION_SERIES = 'V';

/**
 * Everything a "book this transaction" surface needs, without any rendering:
 * the editable rows, description, fiscal year, account options, the money
 * leg from the tenant's chart roles, the engine's booking proposals, the
 * template library, and the POST that creates the verifikat.
 *
 * Used by the bank sheet's and the skattekonto sheet's inline booking
 * views. `init()` is the
 * caller's "the view just opened" signal; nothing is fetched before it.
 *
 * `transaction` is a bank line (`id`) or a Skatteverket line
 * (`statement_id`); the two differ in endpoints and in which chart role
 * supplies the money leg.
 */
export default function useBookingDraft({ companyId, transaction, bankLeg = null, enabled = true }) {
    const transactionType = transaction?.statement_id ? 'skatteverket' : 'bank';
    const statementId = transaction?.statement_id || transaction?.id;

    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [fiscalYears, setFiscalYears] = useState([]);
    const [fiscalYearsError, setFiscalYearsError] = useState(null);
    const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);

    // Engine proposals (the modal's step 1).
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    // Learning-loop join key (§4.5): stamped by the backend when automatic
    // or template suggestions are shown, then echoed on the booking POST.
    const [suggestionGroupId, setSuggestionGroupId] = useState(null);

    // Template library.
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templateError, setTemplateError] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateInputs, setTemplateInputs] = useState({});
    const [templateEvaluating, setTemplateEvaluating] = useState(false);

    // Default leg for the money account: the bank account's MAPPED kontoplan
    // account when the caller supplies one (bank_account_mappings), else the
    // tenant's chart role — primary_bank for bank lines, tax_account for
    // Skatteverket lines. Never a hardcoded number: an unresolved role is an
    // actionable needs-mapping state.
    const [roleLegs, setRoleLegs] = useState(null);
    useEffect(() => {
        if (!companyId || !enabled) return undefined;
        let active = true;
        resolveRoleAccounts(companyId, ['primary_bank', 'tax_account']).then((legs) => {
            if (active) setRoleLegs(legs);
        });
        return () => { active = false; };
    }, [companyId, enabled]);
    const roleLeg = (role) => (roleLegs && roleLegs[role]
        ? { number: roleLegs[role].accountNumber, name: roleLegs[role].accountName }
        : null);
    const defaultLegAccount = transactionType === 'skatteverket'
        ? roleLeg('tax_account')
        : (bankLeg
            ? { number: bankLeg.account_number, name: bankLeg.account_name }
            : roleLeg('primary_bank'));
    const neededRole = transactionType === 'skatteverket' ? 'tax_account' : 'primary_bank';
    const roleLegMissing = Boolean(roleLegs) && !defaultLegAccount;

    const proposalReview = useBookingProposalReview({ transaction, defaultLegAccount });
    const {
        proposal, rows, setRows, selectProposal, startManual, reset: resetProposal,
        balance: { totalDebit, totalCredit, isBalanced },
    } = proposalReview;

    const accountNameFor = useCallback((accountNumber) => {
        const match = accounts.find((option) => option.value === String(accountNumber));
        return match ? match.account_name : '';
    }, [accounts]);

    const fetchFiscalYears = useCallback(async () => {
        setFiscalYearsError(null);
        try {
            const response = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
            const years = response.data.fiscalYears || [];
            setFiscalYears(years);
            // A bank payment belongs to the fiscal year of its booking date.
            const bankDate = transactionType === 'bank' ? String(bankTransactionDate(transaction)).slice(0, 10) : '';
            const current = bankDate
                ? years.find((fy) => String(fy.start_date).slice(0, 10) <= bankDate && String(fy.end_date).slice(0, 10) >= bankDate)
                : years.find((fy) => fy.status === 'active' || fy.is_current);
            setSelectedFiscalYear(current ? current.fiscal_year_id : (bankDate ? null : years[0]?.fiscal_year_id || null));
        } catch (err) {
            console.error('Error fetching fiscal years:', err);
            setFiscalYearsError('Kunde inte hämta räkenskapsår — försök igen');
        }
    }, [companyId, transaction, transactionType]);

    const fetchAccounts = useCallback(async () => {
        try {
            const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts?scope=company`), { withCredentials: true });
            setAccounts((result.data.accounts || []).map((account) => ({
                value: String(account.account_number),
                label: `${account.account_number} - ${account.account_name}`,
                account_number: String(account.account_number),
                account_name: account.account_name,
            })));
        } catch (err) {
            console.error('Error fetching accounts:', err);
        }
    }, [companyId]);

    const fetchTemplates = useCallback(async () => {
        setTemplatesLoading(true);
        setTemplateError(null);
        try {
            setTemplates(await listBookkeepingTemplates(companyId));
        } catch (err) {
            console.error('Error fetching bookkeeping templates:', err);
            setTemplates([]);
            setTemplateError('Kunde inte hämta bokföringsmallar');
        } finally {
            setTemplatesLoading(false);
        }
    }, [companyId]);

    const fetchSuggestions = useCallback(async () => {
        setSuggestionsLoading(true);
        setError(null);
        // Clear before flight — a failed fetch must never leave a previous
        // transaction's suggestions selectable against this line.
        setSuggestions([]);
        setSuggestionGroupId(null);
        resetProposal();
        try {
            const basePath = transactionType === 'skatteverket'
                ? `${companyId}/accounting/reconciliation/skatteverket/statements/${statementId}/verification-suggestions`
                : `${companyId}/accounting/reconciliation/banking/statements/${statementId}/verification-suggestions`;
            const response = await axios.get(getApiUrl(basePath), { withCredentials: true });
            if (response.data.success) {
                setSuggestions(proposalsFromResponse(response.data, {
                    transaction,
                    defaultLegAccount,
                    source: transactionType === 'skatteverket' ? 'tax_authority' : 'bank',
                    accountNameFor,
                }));
                setSuggestionGroupId(response.data.suggestion_group_id || null);
            }
        } catch (err) {
            console.error('Error fetching suggestions:', err);
            setError('Kunde inte hämta kontoförslag');
        } finally {
            setSuggestionsLoading(false);
        }
        // defaultLegAccount/accountNameFor are read at call time on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, statementId, transaction, transactionType, resetProposal]);

    // Build the two prefill rows (money leg + counter leg) for the given
    // counter account. Sign convention: positive amount = money in.
    const buildPrefillRows = useCallback((counterAccountNumber = null, counterAccountName = null) => {
        const amount = round2(Math.abs(parseFloat(transaction?.amount)));
        const incoming = parseFloat(transaction?.amount) > 0;
        // The money-leg role may be unresolved (needs-mapping) or still
        // loading — the manual flow must still open, with an empty account
        // the user fills in (the needs-mapping warning explains why).
        const leg = defaultLegAccount || { number: '', name: '' };
        return [
            {
                account_number: leg.number,
                account_name: accountNameFor(leg.number) || leg.name,
                debit: incoming ? amount : '',
                credit: incoming ? '' : amount,
            },
            {
                account_number: counterAccountNumber ? String(counterAccountNumber) : '',
                account_name: counterAccountNumber ? (accountNameFor(counterAccountNumber) || counterAccountName || '') : '',
                debit: incoming ? '' : amount,
                credit: incoming ? amount : '',
            },
        ];
    }, [transaction, defaultLegAccount, accountNameFor]);

    // A manual start asked for before the money leg resolved: seed the rows
    // once it has (or once we know it never will).
    const [pendingManual, setPendingManual] = useState(false);
    useEffect(() => {
        if (!pendingManual) return;
        if (roleLegs === null && !bankLeg) return;
        startManual(buildPrefillRows());
        setPendingManual(false);
    }, [pendingManual, roleLegs, bankLeg, startManual, buildPrefillRows]);
    // The money leg's name arrives with the accounts list; fill it in once.
    useEffect(() => {
        if (!accounts.length) return;
        setRows((prev) => {
            let changed = false;
            const next = prev.map((row) => {
                if (row.account_number && !row.account_name) {
                    const name = accountNameFor(row.account_number);
                    if (name) { changed = true; return { ...row, account_name: name }; }
                }
                return row;
            });
            return changed ? next : prev;
        });
    }, [accounts, accountNameFor, setRows]);

    const resetAll = useCallback(() => {
        setSuggestions([]);
        setSuggestionGroupId(null);
        resetProposal();
        setSelectedTemplate(null);
        setTemplateInputs({});
        setTemplateError(null);
        setError(null);
        setPendingManual(false);
    }, [resetProposal]);

    /**
     * The view opened. `proposal` (a normalized booking proposal the caller
     * already showed) seeds the editor and keeps the learning-loop link;
     * `initialRows` (≥ 2) seeds the editor directly; `manual` seeds the
     * money leg + one empty row; otherwise the engine's proposals are
     * fetched for a discovery step.
     */
    const init = useCallback(({ initialRows = null, manual = false, proposal: givenProposal = null, description: givenDescription = null } = {}) => {
        if (!transaction) return;
        resetAll();
        fetchFiscalYears();
        fetchAccounts();
        fetchTemplates();
        if (givenProposal) {
            selectProposal(givenProposal);
            setSuggestionGroupId(givenProposal.suggestion_group_id || null);
        } else if (initialRows && initialRows.length >= 2) {
            startManual(initialRows);
        } else if (manual) {
            setPendingManual(true);
        } else {
            fetchSuggestions();
        }
        // Prefill description/notes up front — not inside the suggestion
        // fetch — so a failed suggestion fetch never leaves them empty.
        const sourceLabel = transactionType === 'skatteverket' ? 'skattekontotransaktion' : 'banktransaktion';
        setDescription(givenDescription || (transactionType === 'skatteverket'
            ? transaction.reference || transaction.description
            : bankTransactionLabel(transaction))
            || (transactionType === 'skatteverket' ? 'Skattekontotransaktion' : 'Banktransaktion'));
        setNotes(`Skapad från ${sourceLabel}: ${[transaction.description, transaction.reference].filter(Boolean).join(' · ')}`.trim());
    }, [transaction, transactionType, resetAll, fetchFiscalYears, fetchAccounts, fetchTemplates, fetchSuggestions, startManual, selectProposal]);

    const selectSuggestion = useCallback((suggestion) => {
        setSelectedTemplate(null);
        setTemplateInputs({});
        selectProposal(suggestion);
        setSuggestionGroupId((current) => suggestion.suggestion_group_id || current);
    }, [selectProposal]);

    const startManualRows = useCallback(() => {
        setSelectedTemplate(null);
        setTemplateInputs({});
        startManual(buildPrefillRows());
    }, [startManual, buildPrefillRows]);

    const selectTemplate = useCallback(async (template) => {
        setTemplateError(null);
        setTemplateEvaluating(true);
        try {
            const fullTemplate = template.body ? template : await getBookkeepingTemplate(companyId, template.template_id);
            const initialInputs = {};
            for (const input of fullTemplate.body?.inputs || []) {
                if (input.default !== undefined) initialInputs[input.name] = input.default;
                else if (input.example !== undefined) initialInputs[input.name] = input.example;
                else if (['currency', 'currency_gross', 'currency_net'].includes(input.type)) {
                    initialInputs[input.name] = Math.abs(Number(transaction?.amount) || 0);
                }
            }
            setSelectedTemplate(fullTemplate);
            setTemplateInputs(initialInputs);
        } catch (err) {
            console.error('Error loading bookkeeping template:', err);
            setTemplateError('Kunde inte ladda mallen');
        } finally {
            setTemplateEvaluating(false);
        }
    }, [companyId, transaction]);

    /** Evaluate the selected template; on success the rows are replaced. Returns true when applied. */
    const applyTemplate = useCallback(async () => {
        if (!selectedTemplate) return false;
        setTemplateEvaluating(true);
        setTemplateError(null);
        try {
            const result = await suggestFromTemplate(companyId, selectedTemplate.template_id, templateInputs, {
                statement_id: statementId,
                statement_source: transactionType === 'skatteverket' ? 'tax_authority' : 'bank',
            });
            const templateProposal = proposalFromTemplateResponse(result, selectedTemplate, { transaction, defaultLegAccount, accountNameFor });
            if (!result?.ok || !templateProposal) {
                const message = (result?.errors || []).map((item) => item.error || item.message || String(item)).join(', ');
                setTemplateError(message || 'Mallen kunde inte skapa ett balanserat förslag');
                return false;
            }
            selectProposal(templateProposal);
            setSuggestionGroupId(templateProposal.suggestion_group_id || null);
            return true;
        } catch (err) {
            console.error('Error evaluating bookkeeping template:', err);
            setTemplateError(err.response?.data?.message || 'Kunde inte använda mallen');
            return false;
        } finally {
            setTemplateEvaluating(false);
        }
    }, [companyId, selectedTemplate, templateInputs, statementId, transactionType, transaction, defaultLegAccount, accountNameFor, selectProposal]);

    const updateRow = useCallback((index, field, value) => {
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    }, [setRows]);
    const selectRowAccount = useCallback((index, option) => {
        setRows((prev) => prev.map((row, i) => (i === index
            ? { ...row, account_number: option ? option.account_number : '', account_name: option ? option.account_name : '' }
            : row)));
    }, [setRows]);
    const addRow = useCallback(() => setRows((prev) => [...prev, emptyRow()]), [setRows]);
    const removeRow = useCallback((index) => setRows((prev) => prev.filter((_, i) => i !== index)), [setRows]);

    const rowsAreComplete = rows.length >= 2 && rows.every((row) => (
        row.account_number && ((parseFloat(row.debit) || 0) > 0 || (parseFloat(row.credit) || 0) > 0)
    ));
    const canSubmit = !creating && rowsAreComplete && isBalanced && !!selectedFiscalYear && fiscalYears.length > 0;

    /** POST the verifikat. Resolves with the response data on success, null otherwise. */
    const submitRef = useRef(null);
    const submit = useCallback(async () => {
        if (!selectedFiscalYear) { setError('Välj räkenskapsår'); return null; }
        if (!rowsAreComplete || !isBalanced) { setError('Kontrollera att alla konton är ifyllda och att debet = kredit'); return null; }
        if (submitRef.current) return submitRef.current;
        setCreating(true);
        setError(null);
        const basePath = transactionType === 'skatteverket'
            ? `${companyId}/accounting/reconciliation/skatteverket/statements/${statementId}/verification`
            : `${companyId}/accounting/reconciliation/banking/statements/${statementId}/verification`;
        // Backend contract (audit F1/B1): full editable rows, resolved by
        // account_number server-side.
        const requestBody = {
            rows: rows.map((row) => ({
                account_number: String(row.account_number),
                debit: round2(parseFloat(row.debit) || 0),
                credit: round2(parseFloat(row.credit) || 0),
            })),
            fiscal_year_id: selectedFiscalYear,
            series: VERIFICATION_SERIES,
            description,
            notes,
            // Echo the learning-loop join key so the backend can record the
            // accept/edit outcome against the suggestions it showed (§4.5).
            ...(suggestionGroupId ? { suggestion_group_id: suggestionGroupId } : {}),
            // Only flag used_suggestion when a canonical automatic/template
            // proposal actually seeded the editable rows.
            ...(proposal ? { used_suggestion: true, proposal } : {}),
        };
        submitRef.current = (async () => {
            try {
                const response = await axios.post(getApiUrl(basePath), requestBody, { withCredentials: true });
                if (!response.data.success) return null;
                if (selectedTemplate?.template_id) recordTemplateUsage(companyId, selectedTemplate.template_id);
                return response.data;
            } catch (err) {
                console.error('Error creating verification:', err);
                setError(err.response?.data?.message || 'Kunde inte skapa verifikat');
                return null;
            } finally {
                setCreating(false);
                submitRef.current = null;
            }
        })();
        return submitRef.current;
    }, [companyId, statementId, transactionType, rows, selectedFiscalYear, rowsAreComplete, isBalanced, description, notes, suggestionGroupId, proposal, selectedTemplate]);

    return {
        transactionType,
        statementId,
        // draft
        description, setDescription,
        rows, setRows, updateRow, selectRowAccount, addRow, removeRow,
        proposal, selectSuggestion, startManualRows,
        totalDebit, totalCredit, isBalanced, rowsAreComplete, canSubmit,
        // reference data
        accounts, accountNameFor,
        fiscalYears, fiscalYearsError, fetchFiscalYears, selectedFiscalYear, setSelectedFiscalYear,
        defaultLegAccount, roleLegMissing, neededRole,
        // discovery
        suggestions, suggestionsLoading, suggestionGroupId,
        templates, templatesLoading, templateError, selectedTemplate, setSelectedTemplate,
        templateInputs, setTemplateInputs, templateEvaluating, selectTemplate, applyTemplate,
        // lifecycle
        init, error, setError, creating, submit,
    };
}
